/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/no-cycle */
import * as React from 'react';

import { useDebounce } from 'use-debounce';
import { FlexyFormLabel, FlexyHeaderForm, FlexyInput } from '@europrocurement/flexy-components';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import {
    ConditionsCommercialesJsonldPrescripteurReadConditionsCommercialesFormalites,
    SurchargesCalculsJsonldSurchargesCalculsRead,
    TiersPrescripteurPrescripteurWriteConditionsCommercialesFormalites,
    SurchargesCalculsUpdateSurchargeCalculDtoSurchargesCalculsUpdate,
    SurchargesCalculsCreateSurchargeCalculDtoJsonldSurchargesCalculsWrite,
} from '@europrocurement/l2d-domain/openApi/ApiTiers';
import {
    Box,
    CircularProgress,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import usePromises from '@europrocurement/l2d-hooks/hooks/usePromises';
import {
    regleDeCalculSelector,
    useSociete,
    useTiersService,
    useOffreService,
} from '@europrocurement/l2d-domain';
import {
    facturationAgregee,
    facturationAgregeeDescription,
    facturationAgregeeLibelle,
    facturationComplete,
    facturationCompleteDescription,
    facturationCompleteLibelle,
    facturationSimplifiee,
    facturationSimplifieeDescription,
    facturationSimplifieeLibelle,
} from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/DossierFacturation/functions';
import { formatOptionsByKeys } from '@europrocurement/l2d-modules/modules/Offres/forms/options';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../../redux/types';
import SwitchGroup from './SwitchGroup';

export type BooleanField = {
    label: string;
    value: unknown;
    apiFieldName: string;
};

type CommercialConditionsType = {
    formData: ConditionsCommercialesJsonldPrescripteurReadConditionsCommercialesFormalites;
    isWaitingUpdate: boolean;
};

type FormalitiesSettingsProps = {
    prescriberId: string;
};

const FormalitiesSettings: React.FunctionComponent<FormalitiesSettingsProps> = function (props) {
    const { prescribersApi, surchargesCalculsApi } = useTiersService();
    const { prescriberId } = props;
    const companyId = useSociete();
    const { request, requestState } = useApiRequest();
    const { request: surchargesRequest } = useApiRequest();
    const { request: surchargesUpdateRequest } = useApiRequest();
    const { regleDeCalculsDataSourcesThunks } = useOffreService();
    const dispatch = useAppDispatch();

    // 1 - Component Data / On mount fetch data

    const [commercialConditions, setCommercialConditions] = React.useState<
        CommercialConditionsType | undefined
    >();

    const [isUpdating, setIsUpdating] = React.useState<boolean>(false);

    const [dataFetched, setDataFetched] = React.useState<boolean>(false);

    const [surchargesCalculs, setSurchargesCalculs] = React.useState<
        SurchargesCalculsJsonldSurchargesCalculsRead | undefined
    >();

    const {
        auForfait,
        factureCourrier,
        // depotDossierFtp,
        // emailSousclientObligatoire,
        // signatureElectronique,
        typeFacture,
        typeFacturation,
    } = commercialConditions?.formData || {};

    const id = prescriberId.toString();
    const getSettings = React.useCallback(
        () =>
            prescribersApi.getGrilleForfaitairePrescripteurTiersItem({
                id,
                xIdSociete: companyId,
            }),
        [companyId, id, prescribersApi],
    );

    const { isFetching, isLoaded, fetchedData, call: fetchApi } = usePromises();

    const fetchSurchargesCalculs = React.useCallback(() => {
        surchargesRequest(
            prescribersApi.getPrescriberSurchargesCalculsTiersItem({
                id,
                domaine: 3,
                xIdSociete: companyId,
            }) as any,
            {
                successCallback: (result: any) => {
                    setSurchargesCalculs(result.data[0]);
                },
            },
        );
    }, [companyId, id, prescribersApi, surchargesRequest]);

    React.useEffect(() => {
        fetchSurchargesCalculs();
    }, [fetchSurchargesCalculs]);

    const regleDeCalculStatus = useSelector(regleDeCalculSelector).main.status;

    React.useEffect(() => {
        if (regleDeCalculStatus === 'idle') {
            dispatch(regleDeCalculsDataSourcesThunks.main.getData({}) as any);
        }
    }, [dispatch, regleDeCalculStatus, regleDeCalculsDataSourcesThunks]);

    const formalitiesSettingsPromises = React.useMemo(
        () => [
            {
                name: 'getSettings',
                promise: getSettings(),
                accessorKey: 'conditionsCommerciales',
            },
        ],
        [getSettings],
    );

    React.useEffect(() => {
        fetchApi(formalitiesSettingsPromises);
    }, [fetchApi, formalitiesSettingsPromises]);

    // 2 - Component state & debounce logic

    const updateFormField = (field: string, value: boolean | string | number) => {
        const { formData } = commercialConditions || {};

        if (!formData) {
            return;
        }

        setCommercialConditions({
            formData: {
                ...formData,
                [field]: value,
            },
            isWaitingUpdate: true,
        });
    };

    const [debouncedForm] = useDebounce(commercialConditions, 1000);
    const [skipDebounce, setSkipDebounce] = React.useState<boolean>(false);

    const updateCommercialConditionsRequest = React.useCallback(
        (
            commercialConditionsParams: TiersPrescripteurPrescripteurWriteConditionsCommercialesFormalites,
        ) => {
            setIsUpdating(true);
            return prescribersApi.updateConditionsCommercialesFormalitesPrescripteurTiersItem({
                id: prescriberId,
                tiersPrescripteurPrescripteurWriteConditionsCommercialesFormalites:
                    commercialConditionsParams,
                xIdSociete: companyId,
            });
        },
        [companyId, prescriberId, prescribersApi],
    );

    const updateCommercialConditions = React.useCallback(
        (commercialConditionsParams: {
            conditionsCommerciales: TiersPrescripteurPrescripteurWriteConditionsCommercialesFormalites;
        }) => {
            const successCallback = (
                result: any,
                // result: JsonLDFullFilledResponse['value'] & {
                //     data: TiersPrescripteurPrescripteurWriteConditionsCommercialesFormalites;
                // },
            ) => {
                const updatedCommercialConditions = result.data?.conditionsCommerciales[0];
                setIsUpdating(false);
                setCommercialConditions({
                    formData: updatedCommercialConditions,
                    isWaitingUpdate: false,
                });
            };
            if (!isUpdating) {
                request(updateCommercialConditionsRequest(commercialConditionsParams as any), {
                    successCallback,
                });
            }
        },
        [isUpdating, request, updateCommercialConditionsRequest],
    );

    React.useEffect(() => {
        const { formData, isWaitingUpdate } = commercialConditions || {};

        if (skipDebounce) {
            setSkipDebounce(false);
        } else if (isWaitingUpdate) {
            updateCommercialConditions({
                conditionsCommerciales: [formData] as any,
            });
        }
    }, [commercialConditions, debouncedForm, skipDebounce, updateCommercialConditions]);

    const handleCommercialConditionsData = React.useCallback(
        (
            commercialConditionsData: ConditionsCommercialesJsonldPrescripteurReadConditionsCommercialesFormalites[],
        ) => {
            setCommercialConditions({
                formData: commercialConditionsData[0],
                isWaitingUpdate: false,
            });
        },
        [],
    );

    // 3 - On result / update component state

    React.useEffect(() => {
        if (isLoaded && fetchedData) {
            handleCommercialConditionsData(fetchedData.getSettings ?? []);
            // handleLogoSpecificationsData(fetchedData.getLogoSpecifications ?? []);
            setDataFetched(true);
        }
    }, [isLoaded, fetchedData, handleCommercialConditionsData]);

    // 4 - CommercialConditions update

    const { isProcessing } = requestState || isUpdating;

    // 5 - Render

    // 5a - Form structure / inputs

    const invoicesFlags = [
        {
            label: 'Au forfait',
            value: auForfait,
            apiFieldName: 'auForfait',
        },
        {
            label: 'Envoi postal de la facture',
            value: factureCourrier,
            apiFieldName: 'factureCourrier',
        },
    ];

    // const settingsFlags = [
    //     {
    //         label: 'Dépôt des dossiers via FTP',
    //         value: depotDossierFtp,
    //         apiFieldName: 'depotDossierFtp',
    //     },
    //     {
    //         label: 'Signature électronique',
    //         value: signatureElectronique,
    //         apiFieldName: 'signatureElectronique',
    //     },
    //     {
    //         label: 'Email sous client obligatoire',
    //         value: emailSousclientObligatoire,
    //         apiFieldName: 'emailSousclientObligatoire',
    //     },
    // ];

    const invoiceTypes = [
        {
            label: 'Prescripteur',
            value: 1,
        },
        {
            label: 'Client envoyé au prescripteur',
            value: 2,
        },
        {
            label: 'Prescripteur',
            value: 3,
        },
    ];

    const typesFacturation = [
        {
            label: facturationCompleteLibelle,
            value: facturationComplete,
            description: facturationCompleteDescription,
        },
        {
            label: facturationSimplifieeLibelle,
            value: facturationSimplifiee,
            description: facturationSimplifieeDescription,
        },
        {
            label: facturationAgregeeLibelle,
            value: facturationAgregee,
            description: facturationAgregeeDescription,
        },
    ];

    const fetchedcalculCombineOptions = useSelector(regleDeCalculSelector).main.data;
    const calculCombineOptions = React.useMemo(
        () => [
            { value: null, label: 'Aucune surcharge' },
            ...formatOptionsByKeys(fetchedcalculCombineOptions, 'id', 'libelle'),
        ],
        [fetchedcalculCombineOptions],
    );

    const updateSurchargeCalcul = React.useCallback(
        (field: 'idcalculCombine' | 'idcalculMultiple', calculId: number | null) => {
            const getCalculId = (libelle: string | undefined): number | null => {
                if (!libelle) return null;
                const opt = calculCombineOptions.find((o) => o.label === libelle);
                return opt ? (opt.value as number) : null;
            };

            const idcalculCombine =
                field === 'idcalculCombine'
                    ? calculId
                    : getCalculId(surchargesCalculs?.idcalculCombine?.libelle);
            const idcalculMultiple =
                field === 'idcalculMultiple'
                    ? calculId
                    : getCalculId(surchargesCalculs?.idcalculMultiple?.libelle);

            if (surchargesCalculs?.id !== undefined) {
                const updatePayload: SurchargesCalculsUpdateSurchargeCalculDtoSurchargesCalculsUpdate =
                    { idcalculCombine, idcalculMultiple };
                surchargesUpdateRequest(
                    surchargesCalculsApi.patchSurchargeCalculSurchargesCalculsItem({
                        xIdSociete: companyId,
                        id: String(surchargesCalculs.id),
                        surchargesCalculsUpdateSurchargeCalculDtoSurchargesCalculsUpdate:
                            updatePayload,
                    }) as any,
                    {
                        successCallback: (result: any) => {
                            setSurchargesCalculs(result.data);
                        },
                    },
                );
            } else {
                const createPayload: SurchargesCalculsCreateSurchargeCalculDtoJsonldSurchargesCalculsWrite =
                    {
                        tiers: parseInt(prescriberId, 10),
                        domaine: 3,
                        idcalculCombine,
                        idcalculMultiple,
                    };
                surchargesUpdateRequest(
                    surchargesCalculsApi.postSurchargeCalculSurchargesCalculsCollection({
                        xIdSociete: companyId,
                        surchargesCalculsCreateSurchargeCalculDtoJsonldSurchargesCalculsWrite:
                            createPayload,
                    }) as any,
                    {
                        successCallback: (result: any) => {
                            setSurchargesCalculs(result.data);
                        },
                    },
                );
            }
        },
        [
            calculCombineOptions,
            companyId,
            prescriberId,
            surchargesCalculs,
            surchargesCalculsApi,
            surchargesUpdateRequest,
        ],
    );

    // 5b - render logic

    if (isFetching || dataFetched === false) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '300px',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    // const switchWrapperStyle = {
    //     flex: `1 0 60%`,
    //     marginRight: '15px',
    // };
    console.log(`idcalculCombine${surchargesCalculs?.idcalculCombine?.libelle}`);
    console.log(`idcalculMultiple${surchargesCalculs?.idcalculMultiple?.libelle}`);
    return (
        <Grid
            container
            spacing={4}
            rowSpacing={1}
        >
            <Grid
                item
                xl={12}
                lg={12}
            >
                {/* <FlexyHeaderForm
                    label="Configuration"
                    outlined
                />
                <SwitchGroup
                    booleanFields={settingsFlags}
                    isProcessing={isProcessing}
                    updateFormField={updateFormField}
                    keyPrefix="commercial_conditions_boolean_switch"
                /> */}
                <FlexyHeaderForm
                    label="Surcharge offre"
                    outlined
                />
                <Box
                    display="flex"
                    alignItems="center"
                >
                    <FlexyFormLabel paddingRight={8}>
                        <Typography component="span">Calcul collections combinées</Typography>
                    </FlexyFormLabel>
                    <Box sx={{ flexGrow: 1 }}>
                        <FlexyInput
                            name="prescriber_calculCombine"
                            type="select"
                            options={calculCombineOptions}
                            isClearable
                            value={
                                calculCombineOptions.find(
                                    (opt) =>
                                        opt.label === surchargesCalculs?.idcalculCombine?.libelle,
                                ) ?? null
                            }
                            onChange={(option: { value: unknown; label: string } | null) => {
                                updateSurchargeCalcul(
                                    'idcalculCombine',
                                    option ? (option.value as number) : null,
                                );
                            }}
                        />
                    </Box>
                </Box>
                <Box
                    display="flex"
                    alignItems="center"
                >
                    <FlexyFormLabel paddingRight={8}>
                        <Typography component="span">Calcul collections multiples</Typography>
                    </FlexyFormLabel>
                    <Box sx={{ flexGrow: 1 }}>
                        <FlexyInput
                            name="prescriber_calculMultiple"
                            type="select"
                            options={calculCombineOptions}
                            isClearable
                            value={
                                calculCombineOptions.find(
                                    (opt) =>
                                        opt.label === surchargesCalculs?.idcalculMultiple?.libelle,
                                ) ?? null
                            }
                            onChange={(option: { value: unknown; label: string } | null) => {
                                updateSurchargeCalcul(
                                    'idcalculMultiple',
                                    option ? (option.value as number) : null,
                                );
                            }}
                        />
                    </Box>
                </Box>
                <FlexyHeaderForm
                    label="Facturation"
                    outlined
                />
                <SwitchGroup
                    booleanFields={invoicesFlags}
                    isProcessing={isProcessing}
                    updateFormField={updateFormField}
                    keyPrefix="commercial_conditions_boolean_switch"
                />
                <Box
                    display="flex"
                    alignItems="center"
                >
                    <FlexyFormLabel paddingRight={8}>
                        <Typography component="span">Type de facture</Typography>
                    </FlexyFormLabel>
                    {typeFacturation && (
                        <RadioGroup
                            sx={{ flexGrow: '1' }}
                            aria-labelledby="invoice-type-radio-buttons-group-label"
                            name="invoice-type-buttons-group"
                            value={typeFacturation}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                const newValue = event.target.value;
                                updateFormField('typeFacturation', newValue);
                            }}
                        >
                            <Box
                                display="flex"
                                justifyContent="space-between"
                            >
                                {typesFacturation.map(({ value, label }) => (
                                    <FormControlLabel
                                        value={value}
                                        control={<Radio />}
                                        label={label}
                                        disabled={isProcessing}
                                    />
                                ))}
                            </Box>
                        </RadioGroup>
                    )}
                </Box>
                <Box
                    display="flex"
                    alignItems="center"
                >
                    <FlexyFormLabel paddingRight={8}>
                        <Typography component="span">Destinataire</Typography>
                    </FlexyFormLabel>
                    {typeFacture && (
                        <RadioGroup
                            sx={{ flexGrow: '1' }}
                            aria-labelledby="invoice-type-radio-buttons-group-label"
                            name="invoice-type-buttons-group"
                            value={typeFacture}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                const newValue = parseInt(event.target.value, 10);
                                updateFormField('typeFacture', newValue);
                            }}
                        >
                            <Box
                                display="flex"
                                justifyContent="space-between"
                            >
                                {invoiceTypes.map(({ value, label }) => (
                                    <FormControlLabel
                                        value={value}
                                        control={<Radio />}
                                        label={label}
                                        disabled={isProcessing}
                                    />
                                ))}
                            </Box>
                        </RadioGroup>
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

export default FormalitiesSettings;
